import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { coll } from "@/lib/ddb";
import { useProject } from "@/lib/ddb-hooks";
import {
  LAYOUT_DEFAULTS,
  LAYOUTS,
  getLayout,
  type LayoutId,
  type LayoutOpts,
} from "@/lib/layouts";
import { buildVertexGraph, expandWallSegments, moveVertex, snapToAxis45, snapToVertex, splitWallAtMidpoint, type SolidSegment, type Vertex } from "@/lib/geometry";
import type { Obstacle, Opening, OpeningType, Project, Wall } from "@/lib/types";

const PAD = 24;            // SVG margin in inches
const HANDLE_R = 4;        // vertex handle radius in inches
const MIN_ROOM = 60;       // smallest room dimension for default

type Tool = "select" | "add-wall";

interface DraftWall {
  a: [number, number];
  bX: number;
  bZ: number;
}

export function RoomEditorPanel() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const numId = id ? Number(id) : null;
  const { data: project, isLoading, error } = useProject(numId);

  const [walls, setWallsRaw] = useState<Wall[]>([]);
  const [ceilingHeight, setCeilingHeight] = useState(96);
  const [wallT, setWallT] = useState(4);
  const [tool, setTool] = useState<Tool>("select");
  const [draft, setDraft] = useState<DraftWall | null>(null);
  const [selectedWall, setSelectedWall] = useState<number | null>(null);
  const [snapToGrid, setSnapToGrid] = useState(true);

  // Undo / redo stacks of wall snapshots.
  const undoRef = useRef<Wall[][]>([]);
  const redoRef = useRef<Wall[][]>([]);
  /** setWalls wrapper that pushes the *outgoing* state onto the undo stack
   *  whenever the caller asks for it. Use `silent=true` to suppress (e.g.
   *  intermediate moves during a vertex drag). */
  function setWalls(updater: Wall[] | ((prev: Wall[]) => Wall[]), opts?: { history?: boolean }) {
    setWallsRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (opts?.history) {
        undoRef.current = [...undoRef.current, prev].slice(-50); // cap at 50
        redoRef.current = [];
      }
      return next;
    });
  }
  function undo() {
    const stack = undoRef.current;
    if (stack.length === 0) return;
    const last = stack[stack.length - 1]!;
    undoRef.current = stack.slice(0, -1);
    setWallsRaw((cur) => {
      redoRef.current = [...redoRef.current, cur].slice(-50);
      return last;
    });
  }
  function redo() {
    const stack = redoRef.current;
    if (stack.length === 0) return;
    const last = stack[stack.length - 1]!;
    redoRef.current = stack.slice(0, -1);
    setWallsRaw((cur) => {
      undoRef.current = [...undoRef.current, cur].slice(-50);
      return last;
    });
  }

  // Initialise local state ONCE the project loads. After that, the editor
  // owns walls/ceilingHeight/wallT until the user clicks Save (which is the
  // moment we push back). This avoids polling-driven re-fetches clobbering
  // in-progress edits.
  const initedRef = useRef(false);
  useEffect(() => {
    if (!project || initedRef.current) return;
    initedRef.current = true;
    setWalls(project.room?.walls ?? []);
    setCeilingHeight(project.room?.height ?? 96);
    if (project.room?.walls?.[0]?.thickness) setWallT(project.room.walls[0].thickness);
  }, [project]);

  const dirty = useMemo(() => {
    if (!project) return false;
    return (
      JSON.stringify(walls) !== JSON.stringify(project.room?.walls ?? []) ||
      ceilingHeight !== (project.room?.height ?? 96)
    );
  }, [walls, ceilingHeight, project]);

  // Push ceiling-height change into every wall's `height` field too.
  function setCeilingHeightSyncing(h: number) {
    setCeilingHeight(h);
    setWalls((ws) => ws.map((w) => ({ ...w, height: h })), { history: true });
  }

  function setWallTSyncing(t: number) {
    setWallT(t);
    setWalls((ws) => ws.map((w) => ({ ...w, thickness: t })), { history: true });
  }

  async function save() {
    if (!project) return;
    // Spread the existing room first so obstacles/scan (set by the mobile
    // scanner) survive a wall-only edit on the web.
    await coll("projects").update<Project>(project._id, {
      room: { ...project.room, walls, height: ceilingHeight },
    });
    void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] });
    nav(`/projects/${project._id}/design`);
  }

  function applyPreset(layoutId: LayoutId) {
    const opts: LayoutOpts = { ...LAYOUT_DEFAULTS, ceilingHeight };
    const preset = getLayout(layoutId);
    const room = preset.defaultRoom(opts);
    setWalls(
      room.walls.map((w) => ({ ...w, thickness: wallT, height: ceilingHeight })),
      { history: true },
    );
    setSelectedWall(null);
    setDraft(null);
  }

  function deleteWall(idx: number) {
    setWalls((ws) => ws.filter((_, i) => i !== idx), { history: true });
    setSelectedWall(null);
  }

  function splitSelectedWall() {
    if (selectedWall == null) return;
    setWalls((ws) => splitWallAtMidpoint(ws, selectedWall), { history: true });
    // After split, walls[selectedWall] is the first half; the new wall is
    // walls[selectedWall+1]. Re-select the first half to keep the user oriented.
  }

  function addOpening(type: OpeningType) {
    if (selectedWall == null) return;
    setWalls((ws) => {
      const w = ws[selectedWall];
      if (!w) return ws;
      const L = Math.hypot(w.b[0] - w.a[0], w.b[1] - w.a[1]);
      const defaults: Record<OpeningType, { length: number; sill: number; header: number }> = {
        door:    { length: 36, sill: 0,  header: 84 },
        window:  { length: 36, sill: 36, header: 80 },
        passage: { length: 48, sill: 0,  header: 84 },
      };
      const def = defaults[type];
      const length = Math.min(def.length, Math.max(12, L - 8));
      const startAlong = Math.max(2, (L - length) / 2);
      const opening: Opening = {
        type, startAlong, length,
        sillHeight: def.sill, headerHeight: def.header,
      };
      const existing = w.openings ?? [];
      const next: Wall = { ...w, openings: [...existing, opening] };
      return ws.map((x, i) => i === selectedWall ? next : x);
    }, { history: true });
  }

  function updateOpening(opIdx: number, patch: Partial<Opening>) {
    if (selectedWall == null) return;
    setWalls((ws) => {
      const w = ws[selectedWall];
      if (!w?.openings) return ws;
      const next: Wall = {
        ...w,
        openings: w.openings.map((o, i) => i === opIdx ? { ...o, ...patch } : o),
      };
      return ws.map((x, i) => i === selectedWall ? next : x);
    }, { history: true });
  }

  function deleteOpening(opIdx: number) {
    if (selectedWall == null) return;
    setWalls((ws) => {
      const w = ws[selectedWall];
      if (!w?.openings) return ws;
      const next: Wall = { ...w, openings: w.openings.filter((_, i) => i !== opIdx) };
      return ws.map((x, i) => i === selectedWall ? next : x);
    }, { history: true });
  }

  // Global keyboard shortcuts.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "Escape") { setTool("select"); setDraft(null); setSelectedWall(null); }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedWall != null) {
        e.preventDefault();
        deleteWall(selectedWall);
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedWall]);

  if (!id || Number.isNaN(Number(id))) return <div className="empty-state">Invalid project id.</div>;
  if (isLoading) return <div className="empty-state">Loading project…</div>;
  if (error) return <div className="empty-state">Couldn't load project: {error.message}</div>;
  if (!project) return <div className="empty-state">Project not found.</div>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", height: "100%" }}>
      <SvgFloor
        walls={walls}
        obstacles={project.room?.obstacles ?? []}
        wallT={wallT}
        tool={tool}
        draft={draft}
        selectedWall={selectedWall}
        snapToGrid={snapToGrid}
        onVertexDragStart={() => {
          // snapshot pre-drag state for undo (one entry per drag, not per frame)
          setWalls((ws) => ws, { history: true });
        }}
        onMoveVertex={(v, nx, nz) => setWalls((ws) => moveVertex(ws, v, nx, nz))}
        onStartDraft={(x, z) => setDraft({ a: [x, z], bX: x, bZ: z })}
        onUpdateDraft={(x, z) => setDraft((d) => d ? { ...d, bX: x, bZ: z } : null)}
        onFinishDraft={() => {
          if (!draft) return;
          const len = Math.hypot(draft.bX - draft.a[0], draft.bZ - draft.a[1]);
          if (len >= 6) {
            setWalls((ws) => [...ws, {
              a: draft.a, b: [draft.bX, draft.bZ],
              thickness: wallT, height: ceilingHeight,
            }], { history: true });
          }
          setDraft(null);
          setTool("select");
        }}
        onSelectWall={setSelectedWall}
        onOpeningDragStart={() => { setWalls((ws) => ws, { history: true }); }}
        onUpdateOpening={(wi, oi, patch) => {
          setWalls((ws) => {
            const w = ws[wi];
            if (!w?.openings) return ws;
            const next: Wall = {
              ...w,
              openings: w.openings.map((o, i) => i === oi ? { ...o, ...patch } : o),
            };
            return ws.map((x, i) => i === wi ? next : x);
          });
        }}
      />

      <aside style={{
        borderLeft: "1px solid var(--line)",
        padding: 16,
        overflowY: "auto",
        display: "flex", flexDirection: "column", gap: 16,
      }}>
        <header style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Room</h2>
          <span className="muted" style={{ fontSize: 11 }}>{project.name}</span>
        </header>

        {project.room?.scan && (
          <div style={{
            background: "var(--bg-elev)", padding: 8, borderRadius: 4,
            fontSize: 11, display: "flex", flexDirection: "column", gap: 2,
          }}>
            <div style={{ fontWeight: 600 }}>
              ⌖ Scanned · {project.room.scan.method}
            </div>
            <div className="muted">
              {project.room.scan.device} · {new Date(project.room.scan.capturedAt).toLocaleDateString()}
            </div>
            {(project.room.obstacles?.length ?? 0) > 0 && (
              <div className="muted">
                {project.room.obstacles!.length} obstacle{project.room.obstacles!.length === 1 ? "" : "s"} detected
              </div>
            )}
          </div>
        )}

        <div>
          <div className="label">TOOLS</div>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button
              className="ghost"
              onClick={() => { setTool("select"); setDraft(null); }}
              style={{ flex: 1, borderColor: tool === "select" ? "var(--accent)" : "var(--line)" }}
            >Select / Drag</button>
            <button
              className="ghost"
              onClick={() => { setTool("add-wall"); setSelectedWall(null); }}
              style={{ flex: 1, borderColor: tool === "add-wall" ? "var(--accent)" : "var(--line)" }}
            >+ Add wall</button>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={snapToGrid}
              onChange={(e) => setSnapToGrid(e.target.checked)}
              style={{ width: "auto" }}
            />
            Snap to 1″ grid
          </label>
        </div>

        <div>
          <div className="label">DIMENSIONS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
            <Field label="Ceiling″" value={ceilingHeight} onChange={setCeilingHeightSyncing} />
            <Field label="Wall ″"    value={wallT}         onChange={setWallTSyncing} />
          </div>
        </div>

        <div>
          <div className="label">PRESETS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
            {LAYOUTS.map((l) => (
              <button
                key={l.id}
                className="ghost"
                style={{ fontSize: 11, padding: "4px 8px", textAlign: "left" }}
                onClick={() => applyPreset(l.id)}
                title="Replaces current walls"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {selectedWall != null && walls[selectedWall] && (
          <div className="card" style={{ padding: 12 }}>
            <div className="label">SELECTED WALL #{selectedWall}</div>
            {(() => {
              const w = walls[selectedWall]!;
              const len = Math.hypot(w.b[0] - w.a[0], w.b[1] - w.a[1]);
              const angDeg = (Math.atan2(w.b[1] - w.a[1], w.b[0] - w.a[0]) * 180 / Math.PI + 360) % 360;
              return <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                {len.toFixed(1)}″ · {angDeg.toFixed(0)}°
              </div>;
            })()}

            <div className="label" style={{ marginTop: 12 }}>OPENINGS</div>
            <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
              <button className="ghost" style={{ flex: 1, fontSize: 11, padding: "3px 8px" }} onClick={() => addOpening("door")}>+ Door</button>
              <button className="ghost" style={{ flex: 1, fontSize: 11, padding: "3px 8px" }} onClick={() => addOpening("window")}>+ Window</button>
              <button className="ghost" style={{ flex: 1, fontSize: 11, padding: "3px 8px" }} onClick={() => addOpening("passage")}>+ Passage</button>
            </div>
            {(walls[selectedWall]!.openings ?? []).length === 0 ? (
              <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>No openings — solid wall.</div>
            ) : (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                {(walls[selectedWall]!.openings ?? []).map((op, oi) => {
                  const wallLen = (() => {
                    const w = walls[selectedWall]!;
                    return Math.hypot(w.b[0] - w.a[0], w.b[1] - w.a[1]);
                  })();
                  const maxStart = Math.max(0, wallLen - op.length);
                  const headerY = op.headerHeight ?? (op.type === "window" ? 80 : 84);
                  const sillY   = op.sillHeight   ?? (op.type === "window" ? 36 : 0);
                  return (
                    <div key={oi} style={{ background: "var(--bg-elev)", padding: 6, borderRadius: 4, fontSize: 11 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{op.type}</span>
                        <button
                          className="ghost"
                          style={{ fontSize: 10, padding: "0 6px", color: "var(--danger)" }}
                          onClick={() => deleteOpening(oi)}
                        >×</button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto", columnGap: 4, rowGap: 3, alignItems: "center" }}>
                        <span className="muted">at</span>
                        <input
                          type="number" min={0} max={maxStart} step={1}
                          value={Math.round(op.startAlong)}
                          onChange={(e) => updateOpening(oi, { startAlong: Math.max(0, Math.min(maxStart, +e.target.value)) })}
                          style={{ width: 48 }}
                        />
                        <span className="muted">len</span>
                        <input
                          type="number" min={12} max={wallLen - op.startAlong} step={1}
                          value={Math.round(op.length)}
                          onChange={(e) => updateOpening(oi, { length: Math.max(12, Math.min(wallLen - op.startAlong, +e.target.value)) })}
                          style={{ width: 48 }}
                        />
                        <span className="muted">sill″</span>
                        <input
                          type="number" min={0} max={Math.max(0, headerY - 12)} step={1}
                          value={Math.round(sillY)}
                          onChange={(e) => updateOpening(oi, { sillHeight: Math.max(0, +e.target.value) })}
                          style={{ width: 48 }}
                        />
                        <span className="muted">head″</span>
                        <input
                          type="number" min={sillY + 12} max={96} step={1}
                          value={Math.round(headerY)}
                          onChange={(e) => updateOpening(oi, { headerHeight: Math.max(sillY + 12, +e.target.value) })}
                          style={{ width: 48 }}
                        />
                      </div>
                      {op.type === "window" && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto", columnGap: 4, rowGap: 3, alignItems: "center", marginTop: 4 }}>
                          <span className="muted">rows</span>
                          <input
                            type="number" min={1} max={6} step={1}
                            value={op.panes?.[0] ?? 1}
                            onChange={(e) => {
                              const r = Math.max(1, Math.min(6, +e.target.value));
                              const c = op.panes?.[1] ?? 1;
                              updateOpening(oi, { panes: [r, c] });
                            }}
                            style={{ width: 48 }}
                          />
                          <span className="muted">cols</span>
                          <input
                            type="number" min={1} max={6} step={1}
                            value={op.panes?.[1] ?? 1}
                            onChange={(e) => {
                              const r = op.panes?.[0] ?? 1;
                              const c = Math.max(1, Math.min(6, +e.target.value));
                              updateOpening(oi, { panes: [r, c] });
                            }}
                            style={{ width: 48 }}
                          />
                        </div>
                      )}
                      {op.type === "door" && (
                        <>
                          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                            <button
                              className="ghost"
                              style={{ flex: 1, fontSize: 10, padding: "2px 4px",
                                borderColor: (op.hingeSide ?? "a") === "a" ? "var(--accent)" : "var(--line)" }}
                              onClick={() => updateOpening(oi, { hingeSide: "a" })}
                              title="Hinge at start of opening"
                            >Hinge ←</button>
                            <button
                              className="ghost"
                              style={{ flex: 1, fontSize: 10, padding: "2px 4px",
                                borderColor: op.hingeSide === "b" ? "var(--accent)" : "var(--line)" }}
                              onClick={() => updateOpening(oi, { hingeSide: "b" })}
                              title="Hinge at end of opening"
                            >Hinge →</button>
                            <button
                              className="ghost"
                              style={{ flex: 1, fontSize: 10, padding: "2px 4px",
                                borderColor: (op.swing ?? "in") === "out" ? "var(--accent)" : "var(--line)" }}
                              onClick={() => updateOpening(oi, { swing: (op.swing ?? "in") === "in" ? "out" : "in" })}
                              title="Toggle swing direction"
                            >{(op.swing ?? "in") === "in" ? "Swing in" : "Swing out"}</button>
                          </div>
                          {(() => {
                            const psiDeg = Math.round(((op.doorAngle ?? Math.PI / 2) * 180 / Math.PI));
                            return (
                              <label style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", marginTop: 4 }}>
                                <span className="muted">open</span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <input
                                    type="range"
                                    min={0} max={180} step={5}
                                    value={psiDeg}
                                    onChange={(e) => updateOpening(oi, { doorAngle: (+e.target.value) * Math.PI / 180 })}
                                    style={{ width: 96 }}
                                  />
                                  <span style={{ width: 28, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{psiDeg}°</span>
                                </span>
                              </label>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              className="ghost"
              style={{ marginTop: 12, width: "100%", fontSize: 11 }}
              onClick={splitSelectedWall}
              title="Splits the wall into two halves at the midpoint"
            >Split at midpoint</button>
            <button
              className="ghost"
              style={{ marginTop: 6, color: "var(--danger)", borderColor: "var(--danger)", width: "100%" }}
              onClick={() => deleteWall(selectedWall)}
            >Delete (Del)</button>
          </div>
        )}

        <span style={{ flex: 1 }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {dirty && <div className="muted" style={{ fontSize: 11 }}>● Unsaved changes</div>}
          <button onClick={save} disabled={!dirty}>{dirty ? "Save room" : "Saved"}</button>
          <button className="ghost" onClick={() => nav(`/projects/${project._id}/design`)}>
            ← Back to design
          </button>
        </div>
      </aside>
    </div>
  );
}

function Field({
  label, value, onChange,
}: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label style={{ display: "block" }}>
      <span className="label" style={{ fontSize: 11 }}>{label}</span>
      <input
        type="number"
        value={value}
        min={1}
        step={1}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v) && v > 0) onChange(v);
        }}
      />
    </label>
  );
}

// ── SVG floor ────────────────────────────────────────────────────

interface SvgFloorProps {
  walls: Wall[];
  obstacles: Obstacle[];
  wallT: number;
  tool: Tool;
  draft: DraftWall | null;
  selectedWall: number | null;
  snapToGrid: boolean;
  onVertexDragStart: () => void;
  onMoveVertex: (v: Vertex, nx: number, nz: number) => void;
  onStartDraft: (x: number, z: number) => void;
  onUpdateDraft: (x: number, z: number) => void;
  onFinishDraft: () => void;
  onSelectWall: (idx: number | null) => void;
  onUpdateOpening: (wallIdx: number, opIdx: number, patch: Partial<Opening>) => void;
  onOpeningDragStart: () => void;
}

/** Per-kind palette for obstacle outlines on the 2D plan and 3D scene.
 *  Kept here (not in geometry.ts) because it's purely a presentation choice. */
export const OBSTACLE_COLORS: Record<Obstacle["kind"], string> = {
  outlet:   "#f5c542",  // electrical — amber
  switch:   "#f5c542",
  pipe:     "#9aa5b5",  // neutral — won't necessarily block
  radiator: "#e07a3e",  // warm — blocks placement
  vent:     "#6bbe88",
  column:   "#5b6b80",  // structural — hard block
  beam:     "#5b6b80",
  niche:    "#6b9bd2",  // usable space
  other:    "#b85ab8",
};

function SvgFloor({
  walls, obstacles, wallT, tool, draft, selectedWall, snapToGrid,
  onVertexDragStart, onMoveVertex, onStartDraft, onUpdateDraft, onFinishDraft, onSelectWall,
  onUpdateOpening, onOpeningDragStart,
}: SvgFloorProps) {
  const vertices = useMemo(() => buildVertexGraph(walls), [walls]);
  const svgRef = useRef<SVGSVGElement>(null);
  const [draggingVertex, setDraggingVertex] = useState<Vertex | null>(null);
  const [draggingOpening, setDraggingOpening] = useState<
    { wallIdx: number; opIdx: number; side: "start" | "end" } | null
  >(null);
  // Visual feedback: the vertex the cursor is currently snapping to (or null).
  const [snapHint, setSnapHint] = useState<{ x: number; z: number } | null>(null);
  // Track Shift state so axis-snap can be enforced mid-drag/draft.
  const [shiftHeld, setShiftHeld] = useState(false);
  useEffect(() => {
    function down(e: KeyboardEvent) { if (e.key === "Shift") setShiftHeld(true); }
    function up(e: KeyboardEvent)   { if (e.key === "Shift") setShiftHeld(false); }
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  // Compute SVG viewBox bounds from current walls + obstacles (with margin).
  const bounds = useMemo(() => {
    const xs: number[] = [], zs: number[] = [];
    for (const w of walls) {
      xs.push(w.a[0], w.b[0]);
      zs.push(w.a[1], w.b[1]);
    }
    for (const o of obstacles) {
      // Floor projection: X = center[0] ± half[0], Z = center[2] ± half[2].
      // yaw can push corners outward; use diagonal as worst-case to stay safe.
      const r = Math.hypot(o.half[0], o.half[2]);
      xs.push(o.center[0] - r, o.center[0] + r);
      zs.push(o.center[2] - r, o.center[2] + r);
    }
    if (xs.length === 0) {
      return { x0: -PAD, z0: -PAD, w: 144 + 2 * PAD, h: 108 + 2 * PAD };
    }
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minZ = Math.min(...zs), maxZ = Math.max(...zs);
    const w = Math.max(MIN_ROOM, maxX - minX);
    const h = Math.max(MIN_ROOM, maxZ - minZ);
    return { x0: minX - PAD, z0: minZ - PAD, w: w + 2 * PAD, h: h + 2 * PAD };
  }, [walls, obstacles]);

  function svgToFloor(e: React.PointerEvent<SVGElement>): [number, number] {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const rect = svg.getBoundingClientRect();
    const sx = (e.clientX - rect.left) / rect.width;
    const sy = (e.clientY - rect.top) / rect.height;
    let x = bounds.x0 + sx * bounds.w;
    let z = bounds.z0 + sy * bounds.h;
    if (snapToGrid) {
      x = Math.round(x);
      z = Math.round(z);
    }
    return [x, z];
  }

  function onBackgroundDown(e: React.PointerEvent<SVGElement>) {
    if ((e.target as Element).tagName === "circle") return; // handled by handle
    if ((e.target as Element).hasAttribute("data-wall-idx")) return; // handled by wall
    const [x, z] = svgToFloor(e);
    if (tool === "add-wall") {
      if (!draft) {
        onStartDraft(x, z);
      } else {
        onFinishDraft();
      }
    } else {
      onSelectWall(null);
    }
  }

  function onBackgroundMove(e: React.PointerEvent<SVGElement>) {
    let [x, z] = svgToFloor(e);
    if (draft && e.shiftKey) {
      [x, z] = snapToAxis45(draft.a[0], draft.a[1], x, z);
      if (snapToGrid) { x = Math.round(x); z = Math.round(z); }
    }
    // Vertex-snap: prefer landing exactly on an existing vertex over the
    // grid. Ignore the dragging vertex itself.
    const snapped = snapToVertex(x, z, vertices, 3, draggingVertex?.id);
    if (snapped) {
      x = snapped.x;
      z = snapped.z;
      // Show a pulsing ring on the target while user is in active drag/draft.
      if (draft || draggingVertex) setSnapHint({ x, z });
      else setSnapHint(null);
    } else if (snapHint) {
      setSnapHint(null);
    }

    if (draft) onUpdateDraft(x, z);
    if (draggingVertex) {
      onMoveVertex(draggingVertex, x, z);
    }
    if (draggingOpening) {
      const w = walls[draggingOpening.wallIdx];
      const op = w?.openings?.[draggingOpening.opIdx];
      if (w && op) {
        // Project pointer onto wall axis.
        const dxW = w.b[0] - w.a[0], dzW = w.b[1] - w.a[1];
        const Lw = Math.hypot(dxW, dzW);
        const uxW = dxW / Lw, uzW = dzW / Lw;
        let along = (x - w.a[0]) * uxW + (z - w.a[1]) * uzW;
        if (snapToGrid) along = Math.round(along);
        if (draggingOpening.side === "start") {
          const opEnd = op.startAlong + op.length;
          const newStart = Math.max(0, Math.min(opEnd - 12, along));
          onUpdateOpening(draggingOpening.wallIdx, draggingOpening.opIdx, {
            startAlong: newStart, length: opEnd - newStart,
          });
        } else {
          const opStart = op.startAlong;
          const newEnd = Math.max(opStart + 12, Math.min(Lw, along));
          onUpdateOpening(draggingOpening.wallIdx, draggingOpening.opIdx, {
            length: newEnd - opStart,
          });
        }
      }
    }
  }

  function onVertexDown(e: React.PointerEvent<SVGCircleElement>, v: Vertex) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    onVertexDragStart();
    setDraggingVertex(v);
  }

  // Bound both on individual <circle> vertex handles AND on the root <svg>
  // background (release-anywhere semantics — drop a drag even if the cursor
  // wandered off the handle). Element-generic type covers both.
  function onVertexUp(e: React.PointerEvent<Element>) {
    (e.target as Element).releasePointerCapture(e.pointerId);
    setDraggingVertex(null);
    setDraggingOpening(null);
    setSnapHint(null);
  }

  function onOpeningHandleDown(
    e: React.PointerEvent<SVGCircleElement>,
    wallIdx: number, opIdx: number, side: "start" | "end",
  ) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    onOpeningDragStart();
    setDraggingOpening({ wallIdx, opIdx, side });
  }

  function onWallClick(e: React.MouseEvent, idx: number) {
    e.stopPropagation();
    onSelectWall(idx);
  }

  const gridLines: Array<{ x?: number; z?: number }> = [];
  const gridStep = 12;
  for (let x = Math.ceil(bounds.x0 / gridStep) * gridStep; x <= bounds.x0 + bounds.w; x += gridStep) {
    gridLines.push({ x });
  }
  for (let z = Math.ceil(bounds.z0 / gridStep) * gridStep; z <= bounds.z0 + bounds.h; z += gridStep) {
    gridLines.push({ z });
  }

  return (
    <div style={{ background: "#0e1620", display: "grid", placeItems: "stretch" }}>
      <svg
        ref={svgRef}
        viewBox={`${bounds.x0} ${bounds.z0} ${bounds.w} ${bounds.h}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: "100%", height: "100%",
          cursor: tool === "add-wall" ? "crosshair" : "default",
          touchAction: "none",
        }}
        onPointerDown={onBackgroundDown}
        onPointerMove={onBackgroundMove}
        onPointerUp={onVertexUp}
      >
        {/* Grid */}
        {gridLines.map((g, i) => g.x != null ? (
          <line key={"vx" + i} x1={g.x} y1={bounds.z0} x2={g.x} y2={bounds.z0 + bounds.h}
            stroke="#1b2735" strokeWidth={0.4} />
        ) : (
          <line key={"hz" + i} x1={bounds.x0} y1={g.z} x2={bounds.x0 + bounds.w} y2={g.z}
            stroke="#1b2735" strokeWidth={0.4} />
        ))}

        {/* Walls — solid segments + opening markers */}
        {walls.map((w, idx) => {
          const len = Math.hypot(w.b[0] - w.a[0], w.b[1] - w.a[1]);
          const dx = w.b[0] - w.a[0], dz = w.b[1] - w.a[1];
          const L = Math.max(len, 1e-3);
          const ux = dx / L, uz = dz / L;
          // Perpendicular (interior normal — matches geometry.ts).
          const px = -uz, pz = ux;
          // Outside-of-wall direction for label.
          const off = wallT / 2 + 6;
          const mx = (w.a[0] + w.b[0]) / 2;
          const my = (w.a[1] + w.b[1]) / 2;
          const lx = mx - px * off;
          const ly = my - pz * off;
          const angDeg = Math.atan2(dz, dx) * 180 / Math.PI;
          const flip = angDeg > 90 || angDeg < -90 ? 180 : 0;

          const solidSegs = expandWallSegments([w]) as SolidSegment[];
          const ops = w.openings ?? [];
          const sel = selectedWall === idx;

          return (
            <g key={idx} onClick={(e) => onWallClick(e, idx)} style={{ cursor: "pointer" }}>
              {/* Solid wall pieces */}
              {solidSegs.map((s, si) => (
                <line
                  key={"s" + si}
                  data-wall-idx={idx}
                  x1={s.a[0]} y1={s.a[1]} x2={s.b[0]} y2={s.b[1]}
                  stroke={sel ? "#ffaa55" : "#9aa5b5"}
                  strokeWidth={wallT}
                  strokeLinecap="butt"
                />
              ))}

              {/* Opening markers (door arc / window cross-hatch) */}
              {ops.map((op, oi) => {
                const sx = w.a[0] + op.startAlong * ux;
                const sz = w.a[1] + op.startAlong * uz;
                const ex = w.a[0] + (op.startAlong + op.length) * ux;
                const ez = w.a[1] + (op.startAlong + op.length) * uz;
                if (op.type === "door") {
                  const r = op.length;
                  const hingeAtA  = (op.hingeSide ?? "a") === "a";
                  const swingIn   = (op.swing ?? "in") === "in";
                  const psi       = op.doorAngle ?? Math.PI / 2;
                  const hx = hingeAtA ? sx : ex;
                  const hz = hingeAtA ? sz : ez;
                  const fx = hingeAtA ? ex : sx;
                  const fz = hingeAtA ? ez : sz;
                  // Along (hinge → free corner) unit vector + perpendicular swing vector.
                  const along_dx = (fx - hx) / r;
                  const along_dz = (fz - hz) / r;
                  const nx = swingIn ? px : -px;
                  const nz = swingIn ? pz : -pz;
                  // Door leaf endpoint at current angle.
                  const cosA = Math.cos(psi), sinA = Math.sin(psi);
                  const leafX = hx + r * (cosA * along_dx + sinA * nx);
                  const leafZ = hz + r * (cosA * along_dz + sinA * nz);
                  // 90° swing guide arc (always at perpendicular).
                  const arcX = hx + r * nx;
                  const arcZ = hz + r * nz;
                  const sweep = (hingeAtA && swingIn) || (!hingeAtA && !swingIn) ? 0 : 1;
                  return (
                    <g key={"o" + oi} style={{ pointerEvents: "none" }}>
                      <line x1={sx} y1={sz} x2={ex} y2={ez} stroke="#1f2933" strokeWidth={wallT * 0.6} strokeLinecap="butt" />
                      <line x1={sx} y1={sz} x2={ex} y2={ez} stroke="#d18b53" strokeWidth={0.6} strokeDasharray="2 1" />
                      {/* Quarter-arc swing guide (90° default) */}
                      <path
                        d={`M ${fx} ${fz} A ${r} ${r} 0 0 ${sweep} ${arcX} ${arcZ}`}
                        fill="none" stroke="#d18b53" strokeWidth={0.4} opacity={0.5}
                      />
                      {/* Door leaf at current angle */}
                      <line x1={hx} y1={hz} x2={leafX} y2={leafZ} stroke="#d18b53" strokeWidth={0.8} />
                      <circle cx={hx} cy={hz} r={0.8} fill="#d18b53" />
                    </g>
                  );
                } else if (op.type === "window") {
                  return (
                    <g key={"o" + oi} style={{ pointerEvents: "none" }}>
                      <line x1={sx} y1={sz} x2={ex} y2={ez} stroke="#1f2933" strokeWidth={wallT * 0.6} strokeLinecap="butt" />
                      <line
                        x1={sx + px * (wallT * 0.25)} y1={sz + pz * (wallT * 0.25)}
                        x2={ex + px * (wallT * 0.25)} y2={ez + pz * (wallT * 0.25)}
                        stroke="#6b9bd2" strokeWidth={0.5}
                      />
                      <line
                        x1={sx - px * (wallT * 0.25)} y1={sz - pz * (wallT * 0.25)}
                        x2={ex - px * (wallT * 0.25)} y2={ez - pz * (wallT * 0.25)}
                        stroke="#6b9bd2" strokeWidth={0.5}
                      />
                    </g>
                  );
                } else { // passage
                  return (
                    <g key={"o" + oi} style={{ pointerEvents: "none" }}>
                      <line x1={sx} y1={sz} x2={ex} y2={ez} stroke="#1f2933" strokeWidth={wallT * 0.6} strokeLinecap="butt" />
                      <line x1={sx} y1={sz} x2={ex} y2={ez} stroke="#6bbe88" strokeWidth={0.6} strokeDasharray="3 2" />
                    </g>
                  );
                }
              })}

              {/* Wide transparent hit-area for click-to-select */}
              <line
                data-wall-idx={idx}
                x1={w.a[0]} y1={w.a[1]} x2={w.b[0]} y2={w.b[1]}
                stroke="transparent"
                strokeWidth={wallT + 8}
                strokeLinecap="butt"
              />

              {/* Length label */}
              <text
                x={lx} y={ly}
                fill={sel ? "#ffaa55" : "#9aa5b5"}
                fontSize={4.5}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${angDeg + flip}, ${lx}, ${ly})`}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {len.toFixed(0)}″{ops.length > 0 ? ` · ${ops.length}` : ""}
              </text>
            </g>
          );
        })}

        {/* Obstacles — read-only outlines (radiators, pipes, outlets, columns). */}
        {obstacles.map((o, i) => {
          const cx = o.center[0], cz = o.center[2];
          const w  = o.half[0] * 2, d = o.half[2] * 2;
          const color = OBSTACLE_COLORS[o.kind];
          const yawDeg = o.yaw * 180 / Math.PI;
          return (
            <g key={"obs" + i}
               transform={`rotate(${yawDeg}, ${cx}, ${cz})`}
               style={{ pointerEvents: "none" }}>
              <rect
                x={cx - w / 2} y={cz - d / 2} width={w} height={d}
                fill={color} fillOpacity={0.15}
                stroke={color} strokeWidth={0.6}
                strokeDasharray="2 1.5"
              />
              <text
                x={cx} y={cz}
                fill={color} fontSize={3.5}
                textAnchor="middle" dominantBaseline="middle"
                style={{ userSelect: "none" }}
              >{o.kind}</text>
            </g>
          );
        })}

        {/* Vertex handles */}
        {vertices.map((v) => (
          <circle
            key={v.id}
            cx={v.x} cy={v.z} r={HANDLE_R}
            fill={draggingVertex?.id === v.id ? "#d18b53" : "#6b9bd2"}
            stroke="#1f2933"
            strokeWidth={0.6}
            style={{ cursor: "grab" }}
            onPointerDown={(e) => onVertexDown(e, v)}
            onPointerUp={onVertexUp}
          />
        ))}

        {/* Opening drag-handles (only on the currently-selected wall) */}
        {selectedWall != null && walls[selectedWall]?.openings?.map((op, oi) => {
          const w = walls[selectedWall]!;
          const dxW = w.b[0] - w.a[0], dzW = w.b[1] - w.a[1];
          const Lw = Math.hypot(dxW, dzW);
          const uxW = dxW / Lw, uzW = dzW / Lw;
          const sx = w.a[0] + op.startAlong * uxW;
          const sz = w.a[1] + op.startAlong * uzW;
          const ex = w.a[0] + (op.startAlong + op.length) * uxW;
          const ez = w.a[1] + (op.startAlong + op.length) * uzW;
          const drag = draggingOpening;
          const isStart = drag?.opIdx === oi && drag?.side === "start";
          const isEnd   = drag?.opIdx === oi && drag?.side === "end";
          return (
            <g key={"oh" + oi}>
              <circle
                cx={sx} cy={sz} r={HANDLE_R * 0.85}
                fill={isStart ? "#d18b53" : "#d18b53cc"}
                stroke="#1f2933" strokeWidth={0.6}
                style={{ cursor: "ew-resize" }}
                onPointerDown={(e) => onOpeningHandleDown(e, selectedWall, oi, "start")}
                onPointerUp={onVertexUp}
              />
              <circle
                cx={ex} cy={ez} r={HANDLE_R * 0.85}
                fill={isEnd ? "#d18b53" : "#d18b53cc"}
                stroke="#1f2933" strokeWidth={0.6}
                style={{ cursor: "ew-resize" }}
                onPointerDown={(e) => onOpeningHandleDown(e, selectedWall, oi, "end")}
                onPointerUp={onVertexUp}
              />
            </g>
          );
        })}

        {/* Snap-target ring: shown while dragging/drafting near an existing
            vertex. Larger radius + animated stroke-dasharray makes the snap
            obvious. */}
        {snapHint && (
          <g style={{ pointerEvents: "none" }}>
            <circle
              cx={snapHint.x} cy={snapHint.z} r={HANDLE_R * 2.5}
              fill="none"
              stroke="#d18b53"
              strokeWidth={0.8}
              strokeDasharray="2 1.5"
            >
              <animate attributeName="r" values={`${HANDLE_R * 2}; ${HANDLE_R * 3}; ${HANDLE_R * 2}`} dur="0.8s" repeatCount="indefinite" />
            </circle>
          </g>
        )}

        {/* Draft wall (being placed) */}
        {draft && (
          <>
            <line
              x1={draft.a[0]} y1={draft.a[1]}
              x2={draft.bX}   y2={draft.bZ}
              stroke="#d18b53" strokeWidth={wallT} opacity={0.7}
              strokeDasharray="4 2"
            />
            <circle cx={draft.a[0]} cy={draft.a[1]} r={HANDLE_R} fill="#d18b53" />
            <circle cx={draft.bX}   cy={draft.bZ}   r={HANDLE_R} fill="#d18b53" />
            {(() => {
              const dx = draft.bX - draft.a[0];
              const dz = draft.bZ - draft.a[1];
              const len = Math.hypot(dx, dz);
              const angDeg = (Math.atan2(dz, dx) * 180 / Math.PI + 360) % 360;
              return (
                <text
                  x={(draft.a[0] + draft.bX) / 2}
                  y={(draft.a[1] + draft.bZ) / 2 - 4}
                  fill="#fff"
                  fontSize={5}
                  textAnchor="middle"
                  style={{ pointerEvents: "none" }}
                >
                  {len.toFixed(0)}″ · {angDeg.toFixed(0)}°{shiftHeld ? " ⤓" : ""}
                </text>
              );
            })()}
          </>
        )}

        {/* North arrow + scale hint */}
        <g transform={`translate(${bounds.x0 + 10},${bounds.z0 + 16})`}>
          <text fill="#5b6b80" fontSize={5}>N ↑</text>
          <text x={0} y={8} fill="#3a4555" fontSize={4}>grid 12″</text>
        </g>
      </svg>
    </div>
  );
}
